import { Outlet } from 'react-router-dom';
import { LoadingSpinner } from '@demand-iq/ui';
import useHasApp from 'hooks/useHasApp';
import AppNotAvailable from 'pages/AppNotAvailable';
import NotFound from 'pages/NotFound';

type RequireAppProps = Readonly<{
  clientId: string;
}>;

/**
 * Layout route that gates a whole subtree on app entitlement:
 *
 *   <Route element={<RequireApp clientId="polaris" />}>
 *     <Route path={workflowLanding} element={...} />
 *   </Route>
 *
 * Mounting it as a layout route keeps App.tsx flat -- no per-route wrapping, and no reading of
 * window.location to decide what is protected.
 */
const RequireApp = ({ clientId }: RequireAppProps): JSX.Element => {
  const hasApp = useHasApp();
  const { hasApp: allowed, companyOwnsApp, isPending } = hasApp(clientId);

  // Never show the reject page while a query is still in flight, or every hard refresh would
  // briefly deny an entitled user.
  if (isPending) {
    return <LoadingSpinner className="h-64" />;
  }

  // A company that does not own the app gets the same 404 as a URL that was never a route: naming
  // the product, or offering to sell it, told an unentitled visitor that the route is real and what
  // lives behind it (ENG-1169). Only a user whose own company already owns the app sees a reason --
  // their admin can grant the seat, and saying so discloses nothing they cannot already see.
  if (!allowed) {
    return companyOwnsApp ? <AppNotAvailable clientId={clientId} /> : <NotFound inShell />;
  }

  return <Outlet />;
};

export default RequireApp;
